import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { CheckIcon, ChevronDownIcon } from "../../../../../Icons";
import { fieldClass } from "../formLayout";
import {
  DAILY_LOG_VISIT_CODE_GROUPS,
  DAILY_LOG_VISIT_CODES,
} from "./dailyLogContent";

/**
 * Code field: grouped dropdown (Billable / Non-Billable / Office) + free typing.
 * Menu is portaled to document.body so parent overflow:hidden cannot clip it.
 */
const VisitCodeCombobox = ({
  value = "",
  onChange,
  disabled = false,
  className = "",
  placeholder = "Select",
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const current = String(value || "");
  const knownSelected = useMemo(
    () => DAILY_LOG_VISIT_CODES.includes(current),
    [current],
  );

  const updateMenuPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.max(rect.width, 152);
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferUp = spaceBelow < 220 && rect.top > spaceBelow;
    const maxHeight = Math.min(256, preferUp ? rect.top - 8 : spaceBelow - 8);

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width,
      zIndex: 80,
      maxHeight: Math.max(120, maxHeight),
      ...(preferUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const t = event.target;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (code) => {
    onChange?.(code);
    setOpen(false);
    inputRef.current?.focus?.();
  };

  const menu =
    open && !disabled && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            style={menuStyle}
            className="overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg py-1"
          >
            <p className="px-2.5 py-1 text-[10px] font-semibold text-slate-500">
              Select
            </p>
            {DAILY_LOG_VISIT_CODE_GROUPS.map(({ group, codes }) => (
              <div key={group} className="pb-0.5">
                <p className="px-2.5 pt-1.5 pb-0.5 text-[10px] font-medium text-slate-400">
                  {group}
                </p>
                {codes.map((code) => {
                  const selected = current === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`w-full flex items-center gap-1.5 px-2.5 py-1 text-left text-[12px] font-semibold ${
                        selected
                          ? "bg-indigo-600 text-white"
                          : "text-slate-800 hover:bg-slate-50"
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pick(code)}
                    >
                      <span className="w-3.5 shrink-0 flex items-center justify-center">
                        {selected ? <CheckIcon size={12} /> : null}
                      </span>
                      {code}
                    </button>
                  );
                })}
              </div>
            ))}
            {current.trim() && !knownSelected ? (
              <div className="mt-0.5 border-t border-slate-100 px-2.5 py-1.5">
                <p className="text-[10px] text-slate-400">
                  Custom:{" "}
                  <span className="font-semibold text-slate-700">
                    {current.trim()}
                  </span>
                </p>
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          className={`${fieldClass} pr-7`}
          value={current}
          placeholder={placeholder}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onChange={(event) => onChange?.(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && knownSelected) {
              event.preventDefault();
              setOpen(false);
            }
          }}
          aria-expanded={open}
          aria-haspopup="listbox"
          role="combobox"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Open code list"
          className="absolute inset-y-0 right-0 w-7 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-40"
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => !prev);
            inputRef.current?.focus?.();
          }}
        >
          <ChevronDownIcon
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {menu}
    </div>
  );
};

VisitCodeCombobox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

export default VisitCodeCombobox;
